/**
 * ContentPreviewCard — renders a content_publish payload as the platform's
 * native post card so approvers can review exactly what will go public.
 *
 * Supports: Twitter (X), LinkedIn, Instagram, YouTube.  The visual fidelity
 * is intentionally high — we mimic each platform's signature font weight,
 * border-radius, and avatar+handle layout so the approver doesn't need to
 * cross-check against the real site.
 */
import React from 'react';
import { Twitter, Linkedin, Instagram, Youtube, Link2, Calendar, AtSign } from 'lucide-react';

const PLATFORM_META = {
  twitter:   { Icon: Twitter,   label: 'Twitter (X)',  accent: '#1DA1F2', max: 280  },
  linkedin:  { Icon: Linkedin,  label: 'LinkedIn',     accent: '#0A66C2', max: 3000 },
  instagram: { Icon: Instagram, label: 'Instagram',    accent: '#E4405F', max: 2200 },
  youtube:   { Icon: Youtube,   label: 'YouTube',      accent: '#FF0000', max: 5000 },
};

function fmtScheduled(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  }); } catch { return iso; }
}

export default function ContentPreviewCard({ payload, authorName = 'Vigilant Entities', authorHandle = '@vigilantentities' }) {
  if (!payload) return null;
  const meta = PLATFORM_META[payload.platform] || PLATFORM_META.twitter;
  const { Icon, label, accent } = meta;
  const handle = payload.target_account || authorHandle;
  const isImageMedia = !!payload.media_data_url;

  return (
    <div className="rounded-lg overflow-hidden border-2 border-slate-200 bg-white max-w-md shadow-sm"
         data-testid="content-preview-card"
         data-platform={payload.platform}>
      {/* Platform header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100"
           style={{ background: `${accent}10` }}>
        <Icon size={16} style={{ color: accent }}/>
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: accent }}>
          {label} · Preview
        </span>
        {payload.scheduled_at && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-500">
            <Calendar size={10}/> {fmtScheduled(payload.scheduled_at)}
          </span>
        )}
      </div>

      {/* Twitter / LinkedIn — author header */}
      {(payload.platform === 'twitter' || payload.platform === 'linkedin') && (
        <div className="flex items-start gap-3 px-4 pt-3">
          <div className="h-10 w-10 rounded-full bg-[#0A0A0A] flex items-center justify-center shrink-0">
            <span className="text-[#8B3FC5] font-bold text-sm">VE</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm leading-tight">{authorName}</div>
            <div className="text-xs text-slate-500 leading-tight inline-flex items-center gap-1">
              <AtSign size={10}/>{handle.replace(/^@/, '')}
            </div>
          </div>
        </div>
      )}

      {/* Instagram — media first, then caption */}
      {payload.platform === 'instagram' && isImageMedia && (
        <img src={payload.media_data_url} alt="Post media"
             className="w-full aspect-square object-cover bg-slate-100"/>
      )}
      {payload.platform === 'instagram' && !isImageMedia && payload.video_url && (
        <div className="aspect-square bg-black flex items-center justify-center text-white text-xs">
          <Youtube size={32} className="opacity-60"/>
          <span className="ml-2">{payload.video_url}</span>
        </div>
      )}

      {/* YouTube — thumbnail + title style */}
      {payload.platform === 'youtube' && (
        <div className="bg-slate-900 text-white">
          {isImageMedia ? (
            <img src={payload.media_data_url} alt="Thumbnail" className="w-full aspect-video object-cover"/>
          ) : (
            <div className="aspect-video flex items-center justify-center">
              <Youtube size={48} className="text-red-500"/>
            </div>
          )}
        </div>
      )}

      {/* Body text */}
      <div className="px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed text-slate-800">
        {payload.body_text}
      </div>

      {/* Link card */}
      {payload.link_url && (
        <a href={payload.link_url} target="_blank" rel="noopener noreferrer"
           className="mx-4 mb-3 flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-xs text-slate-700 hover:bg-slate-50 break-all">
          <Link2 size={12} style={{ color: accent }}/> {payload.link_url}
        </a>
      )}

      {/* Twitter / LinkedIn media after text */}
      {(payload.platform === 'twitter' || payload.platform === 'linkedin') && isImageMedia && (
        <img src={payload.media_data_url} alt="Post media"
             className="w-full max-h-72 object-cover border-t border-slate-100"/>
      )}

      {/* Footer — character counter for Twitter */}
      {payload.platform === 'twitter' && (
        <div className="px-4 py-2 border-t border-slate-100 text-[10px] uppercase tracking-[0.18em] font-bold flex justify-between"
             style={{ color: accent }}>
          <span>{(payload.body_text || '').length} / {meta.max} chars</span>
          {payload.notes && <span className="text-slate-400 normal-case tracking-normal">internal note attached</span>}
        </div>
      )}
    </div>
  );
}
